import { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import Peer from "simple-peer";
import "./view.css";
import { useStream } from "../../context/StreamContext";
import Loading from "../../layout/Loading";
import { createRTCClient } from "../RtcClientSdk";
import isMobileDevice from "../../utils/isMobileDevice";
import {
  INFORMATION_PAGE,
  LANDING_PAGE,
  MOB_DEVICE_SIGNAL_STORAGE_NAME,
} from "../../Constants";
import ViewerOptions from "./ViewerOptions";
import QRScanner from "./QRScanner";
import showViewerOnboardingTour from "../../layout/MobileOnboarding";

function ViewSession() {
  const [stream, setStream] = useStream();
  const [me, setMe] = useState("");
  const [connectionInfo, setConnectionInfo] = useState({
    caller: "",
    callerSignal: null,
  });
  const [callAccepted, setCallAccepted] = useState(false);
  const [sessionInfoUI, setSessionInfoUI] = useState();
  const connectionRef = useRef({});
  const clientSocketRef = useRef();
  const MOB_STORE_INFO = JSON.parse(
    localStorage.getItem(MOB_DEVICE_SIGNAL_STORAGE_NAME),
  );

  async function initRtcClient(roomIdToJoin) {
    const client = await createRTCClient(roomIdToJoin, null, null);
    client.on("user-published", (userStream, mediaType) => {
      handleUserPublished(userStream, mediaType, client, roomIdToJoin);
    });
    client.on("user-unpublished", (userStream, mediaType) => {
      const updateLocalStore = { ...MOB_STORE_INFO, roomId: null };
      localStorage.removeItem(MOB_DEVICE_SIGNAL_STORAGE_NAME);
      localStorage.setItem(
        MOB_DEVICE_SIGNAL_STORAGE_NAME,
        JSON.stringify(updateLocalStore),
      );
    });
  }

  async function handleUserPublished(
    userStreamInfo,
    mediaType,
    client,
    roomId,
  ) {
    await client.subscribe(userStreamInfo, mediaType);
    console.log("Stream aquired:", userStreamInfo);
    clientSocketRef.current.emit("acceptCall", {
      signal: null,
      to: roomId,
    });
    setStream(userStreamInfo);
  }

  useEffect(() => {
    showViewerOnboardingTour();
  }, []);

  useEffect(() => {
    const isMobileDeviceUser = isMobileDevice();
    if (!isMobileDeviceUser) {
      window.location.href = LANDING_PAGE;
    }

    if (isMobileDeviceUser && !stream) {
      clientSocketRef.current = io.connect(
        process.env.REACT_APP_BE_HOST + ":" + process.env.REACT_APP_BE_PORT,
      );

      clientSocketRef.current.on("stream-provider-info", (data) => {
        localStorage.setItem(
          MOB_DEVICE_SIGNAL_STORAGE_NAME,
          JSON.stringify(data),
        );

        if (data.streamThreading === "multi-threaded") {
          initRtcClient(data.roomId);
        }
      });

      clientSocketRef.current.on("connect_error", () => {
        //TODO: Go to some error page
      });

      clientSocketRef.current.on("error", (err) => {
        console.log("Server socket error:", err);
      });

      clientSocketRef.current.on("me", (id) => {
        setMe(id);
        window.scrollTo(0, 0);
      });

      clientSocketRef.current.on("req-session", (data) => {
        console.log(data.signal);
        setConnectionInfo((state) => {
          return { ...state, caller: data.from, callerSignal: data.signal };
        });
      });
    }
    return () => {
      if (clientSocketRef.current) {
        clientSocketRef.current.disconnect();
      }
    };
  }, [stream]);

  useEffect(() => {
    function acceptCall() {
      setCallAccepted(true);
      setSessionInfoUI(null);
      const peer = new Peer({
        initiator: false,
        trickle: false,
      });

      peer.on("signal", (data) => {
        console.log(data);
        clientSocketRef.current.emit("acceptCall", {
          signal: data,
          to: connectionInfo.caller,
        });
      });

      peer.on("stream", (stream) => {
        setSessionInfoUI(<Loading text={"Loading..."} />);
        setTimeout(() => {
          setStream(stream);
          setSessionInfoUI(null);
        }, 5000);
      });

      peer.on("error", (err) => {
        console.log("P2P connection error details:", err);
        clientSocketRef.current.emit("P2PConnectionError", {
          error: err,
          to: connectionInfo.caller,
        });
      });

      if (connectionInfo.callerSignal !== undefined) {
        peer.signal(connectionInfo.callerSignal);
      }

      connectionRef.current = peer;
    }

    function denyCall() {
      clientSocketRef.current.emit("denyCall", {
        to: connectionInfo.caller,
      });
      setConnectionInfo((state) => ({ ...state, callerSignal: undefined }));
      reloadPage();
    }

    var connectionTimeout;
    if (connectionInfo.callerSignal && !callAccepted) {
      connectionTimeout = setTimeout(() => {
        setSessionInfoUI(<Loading text={"Establishing stream"} />);
        setTimeout(() => {
          setSessionInfoUI(<Loading text={"Loading UI"} />);
          setTimeout(() => {
            setSessionInfoUI(<Loading text={"Obtaining stream"} />);
            setTimeout(() => {
              setSessionInfoUI(
                <>
                  <h4>Session requeset recieved. Would you like to accept? </h4>
                  <br />
                  <a className="buttonPrimary" onClick={acceptCall}>
                    Accept
                  </a>
                  <a className="buttonRed" onClick={denyCall}>
                    Deny
                  </a>
                </>,
              );
            }, 15000);
          }, 1000);
        }, 2000);
      }, 1000);
    }

    return () => {
      clearTimeout(connectionTimeout);
    };
  }, [connectionInfo, callAccepted, setStream]);

  const getPairingPin = () => {
    window.scrollTo(0, 0);
    if (!!me) {
      return me;
    }
    return <Loading />;
  };

  const reloadPage = () => {
    window.location.reload();
  };

  const handleReconnectionToStream = () => {
    if (MOB_STORE_INFO.roomId) {
      initRtcClient(MOB_STORE_INFO.roomId);
    }
  };

  return (
    <div className="container">
      {!stream ? (
        <div className="container paddingtop20 ">
          <QRScanner pin={me} io={clientSocketRef.current} />
          <h1 className="extra-large-font font-weight-normal ">
            Your Pairing Pin
          </h1>
          <h1 id="pairing-pin" className="extra-large-font">
            {" "}
            {getPairingPin()}{" "}
            <span>
              <i
                onClick={reloadPage}
                class="fa-large fa-pointer fa-solid fa-arrow-rotate-right"
              ></i>
            </span>
          </h1>
          {MOB_STORE_INFO &&
            MOB_STORE_INFO.roomId &&
            MOB_STORE_INFO.streamThreading === "multi-threaded" && (
              <div className="paddingtop20">
                <a
                  href="#"
                  className="buttonPrimary"
                  onClick={handleReconnectionToStream}
                >
                  Reconnect
                </a>
              </div>
            )}
          <h3 className="font-weight-normal paddingtop20">
            <i>
              Head over to{" "}
              <b>
                {process.env.REACT_APP_FE_HOST ?? "https://head3dio.com"}/home
              </b>{" "}
              on PC to securly connect
            </i>
          </h3>
          <div className="paddingtop20">
            <a href={INFORMATION_PAGE} className="buttonPrimary">
              Need more help?
            </a>
          </div>
          {sessionInfoUI && <div className="paddingtop20">{sessionInfoUI}</div>}
        </div>
      ) : (
        <ViewerOptions stream={stream} sessionInfoUI={sessionInfoUI} />
      )}
    </div>
  );
}

export default ViewSession;
